/* PhaseRemindersBell — iter425. Bell icon с unread badge + dropdown drawer. */
import React, { useCallback, useEffect, useState } from "react";
import { useAuth } from "@/App";
import { useLanguage } from "@/i18n/LanguageContext";
import { useTheme } from "@/theme/ThemeContext";
import { Bell, BellRing, X, Sparkles, Loader2 } from "lucide-react";
import axios from "axios";
import { toast } from "sonner";

export default function PhaseRemindersBell() {
  const { API, user } = useAuth();
  const { language } = useLanguage();
  const { isDark } = useTheme();
  const bg = language === "bg";

  const [items, setItems] = useState([]);
  const [unread, setUnread] = useState(0);
  const [open, setOpen] = useState(false);

  const fetchReminders = useCallback(async () => {
    try {
      const r = await axios.get(`${API}/cycle-aware/reminders`);
      setItems(r.data?.items || []);
      setUnread(r.data?.unread_count || 0);
    } catch (_e) {
      // silent — feature is optional
    }
  }, [API]);

  // On mount: try refresh + load
  useEffect(() => {
    if (!user) return;
    let alive = true;
    (async () => {
      try {
        await axios.post(`${API}/cycle-aware/reminders/refresh`);
      } catch (_e) {}
      if (alive) fetchReminders();
    })();
    return () => { alive = false; };
  }, [API, user, fetchReminders]);

  const handleDismiss = async (id, e) => {
    e?.stopPropagation?.();
    setItems((arr) => arr.filter((r) => r.id !== id));
    setUnread((u) => Math.max(0, u - 1));
    try {
      await axios.post(`${API}/cycle-aware/reminders/${id}/dismiss`);
    } catch (_e) {
      toast.error(bg ? "Грешка" : "Failed");
    }
  };

  const handleMarkRead = async (id) => {
    try {
      await axios.post(`${API}/cycle-aware/reminders/${id}/mark-read`);
      setItems((arr) => arr.map((r) => (r.id === id ? { ...r, read: true } : r)));
      setUnread((u) => Math.max(0, u - 1));
    } catch (_e) {}
  };

  if (!user || items.length === 0) {
    // Tiny placeholder bell — shows nothing если липсват напомняния
    return null;
  }

  return (
    <div className="relative" data-testid="phase-reminders-bell">
      <button onClick={() => setOpen((o) => !o)}
              className={`relative w-9 h-9 rounded-xl flex items-center justify-center transition-colors ${
                isDark ? "hover:bg-slate-800 text-slate-300" : "hover:bg-slate-100 text-slate-700"
              }`}
              data-testid="phase-bell-btn"
              title={bg ? "Фаза-напомняния" : "Phase reminders"}>
        {unread > 0 ? <BellRing className="w-4.5 h-4.5" /> : <Bell className="w-4.5 h-4.5" />}
        {unread > 0 && (
          <span className="absolute -top-0.5 -right-0.5 w-4 h-4 rounded-full bg-rose-500 text-white text-[9px] font-bold flex items-center justify-center"
                data-testid="phase-bell-badge">
            {unread}
          </span>
        )}
      </button>

      {open && (
        <div className={`absolute right-0 mt-2 w-80 max-h-[480px] overflow-y-auto rounded-2xl shadow-2xl border z-50 ${
          isDark ? "bg-slate-900 border-slate-700" : "bg-white border-slate-200"
        }`} data-testid="phase-reminders-drawer">
          <div className={`flex items-center justify-between px-3 py-2 border-b ${isDark ? "border-slate-800" : "border-slate-200"}`}>
            <p className={`text-xs font-bold flex items-center gap-1 ${isDark ? "text-white" : "text-slate-900"}`}>
              <Sparkles className="w-3.5 h-3.5 text-fuchsia-500" />
              {bg ? "Фаза Coach" : "Phase Coach"}
            </p>
            <button onClick={() => setOpen(false)}
                    className={`w-6 h-6 rounded flex items-center justify-center ${isDark ? "hover:bg-slate-800 text-slate-400" : "hover:bg-slate-100 text-slate-500"}`}>
              <X className="w-3.5 h-3.5" />
            </button>
          </div>
          <div className="p-2 space-y-2">
            {items.map((r) => (
              <div key={r.id}
                   onClick={() => !r.read && handleMarkRead(r.id)}
                   className={`p-2.5 rounded-xl cursor-pointer transition-colors ${
                     r.read
                       ? (isDark ? "bg-slate-800/40" : "bg-slate-50")
                       : (isDark ? "bg-fuchsia-500/10 border border-fuchsia-500/30" : "bg-fuchsia-50 border border-fuchsia-200")
                   }`}
                   data-testid={`phase-reminder-${r.id}`}>
                <div className="flex items-start gap-2">
                  <div className="flex-1 min-w-0">
                    <p className={`text-[11.5px] font-bold leading-snug ${isDark ? "text-white" : "text-slate-900"}`}>
                      {r.title}
                    </p>
                    <p className={`text-[10.5px] leading-relaxed mt-1 ${isDark ? "text-slate-300" : "text-slate-700"}`}>
                      {r.body}
                    </p>
                    <p className={`text-[9px] mt-1 ${isDark ? "text-slate-500" : "text-slate-500"}`}>
                      {r.phase_name_bg} · ден {r.cycle_day}
                    </p>
                  </div>
                  <button onClick={(e) => handleDismiss(r.id, e)}
                          data-testid={`phase-reminder-dismiss-${r.id}`}
                          className={`flex-shrink-0 w-5 h-5 rounded flex items-center justify-center ${
                            isDark ? "hover:bg-slate-700 text-slate-400" : "hover:bg-slate-200 text-slate-500"
                          }`}
                          title={bg ? "Изтрий" : "Dismiss"}>
                    <X className="w-3 h-3" />
                  </button>
                </div>
              </div>
            ))}
          </div>
        </div>
      )}
    </div>
  );
}
